import React from 'react';
import { get, isArray } from 'lodash';
import { Tooltip, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { InfoOutlined as InfoIcon } from '@mui/icons-material';

import Button from '../../../common/components/Button/Button';

export default function InfoButtonComponent({ infoOption, customOption, isAbsolute = true, withoutSorting = false }) {
  const [isOpen, setIsOpen] = React.useState(false);

  const infoIconStyle = {
    cursor: 'pointer',
    color: '#2196F3',
    position: isAbsolute ? 'absolute' : 'static',
  };
  const handleClickOpen = () => setIsOpen(true);

  const handleClose = () => setIsOpen(false);

  const infoOptions = {
    chargerStatuses: {
      title: 'Charger Statuses',
      text: [
        {
          key: 'Available',
          description: 'all Connectors are in Available State.',
        },
        {
          key: 'Alert',
          description: 'at least 1 Connector is in Alert State.',
        },
        {
          key: 'Partially out of service',
          description: 'at least 1 Connector is in Alarm State.',
        },
        {
          key: 'Totally out of service',
          description: 'all Connectors are in Alarm State.',
        },
        {
          key: 'Unknown',
          description: 'charger is offline.',
        },
        {
          key: 'Monitored',
          description: 'all Connectors are back to Available State after a successful charging session.',
        },
      ],
    },
    firstConnectorState: {
      title: 'Connector State',
      text: [
        {
          key: 'Available',
          description: 'Charger is online.',
        },
        {
          key: 'Unknown',
          description: 'Charger is offline.',
        },
      ],
    },
    otherConnectorState: {
      title: 'Connector State',
      text: [
        {
          key: 'Available',
          description: 'Connector had no errors after successful charging session.',
        },
        {
          key: 'Alert',
          description: 'Connector had 5 non-critical errors or 1 semi-critical error.',
        },
        {
          key: 'Alarm',
          description: 'Connector had 1 critical error.',
        },
        {
          key: 'Unknown',
          description: 'Charger is offline.',
        },
      ],
    },
    sessionStatusesState: {
      title: 'Session State',
      text: [
        {
          key: 'Ok',
          description: 'Session successfully ended.',
        },
        {
          key: 'In Progress',
          description: 'Session is in progress.',
        },
        {
          key: 'Local Stop',
          description: 'Session stopped locally by presenting a User Id tag.',
        },
        {
          key: 'Remote Stop',
          description: 'Session stopped remotely, for instance, via OCPP.',
        },
        {
          key: 'Start Error',
          description: 'Session didn’t start. The issues could be related to establishing the V2G connection.',
        },
        {
          key: 'Stop Error',
          description: 'Session stopped due to an error encountered during charging or preparation for charging.',
        },
        {
          key: 'EV Disconnected',
          description: 'Session stopped because the cable was disconnected or the vehicle moved too far from the charger.',
        },
        {
          key: 'Emergency Stop',
          description: 'Session was stopped by pressing the emergency stop button.',
        },
        {
          key: 'Energy Limit Reached',
          description: 'Session stopped due to energy delivery failure.',
        },
        {
          key: 'Power Loss',
          description: 'Session stopped because the charging session reached the local energy transfer limit.',
        },
        {
          key: 'Reboot',
          description: 'Session stopped because someone rebooted the device (initiated via IoT.ON, OCPP, or locally).',
        },
        {
          key: 'Soft Reset',
          description: 'Session stopped due to the “Soft” Reset performed via OCPP.',
        },
        {
          key: 'Hard Reset',
          description: 'Session stopped due to “Hard” Reset performed via OCPP.',
        },
        {
          key: 'Unlock Command',
          description: 'Session stopped due to UnlockConnector command received from OCPP.',
        },
        {
          key: 'Deauthorized',
          description: 'Session stopped due to OCPP authorization being revoked (e.g. token expired).',
        },
        {
          key: 'Cloud Cmd',
          description: 'Session stopped due to Command from IoT.ON.',
        },
        {
          key: 'Other',
          description: 'Other.',
        },
        {
          key: 'Session Finished',
          description: 'Session finished to reflect the real-time state of the charger.',
        },
        {
          key: 'Suspended EVSE',
          description:
            'Session is suspended due to smart charging restrictions, local power supply constraints, or instructions from StartTransaction.conf indicating that charging is prohibited.',
        },
        {
          key: 'Suspended EV',
          description: 'Session is suspended due to EV not consuming any energy.',
        },
        {
          key: 'Error',
          description: 'Session stopped due to an error encountered during charging or preparation for charging.',
        },
      ],
    },
    batchUpdateDetails: {
      title: 'Template and Additional Rules',
      text: [
        {
          key: 'Template Rules',
          description: [
            {
              text: 'The spreadsheet template contains a list of Charger parameters that are available for updating through the uploading mechanism.',
            },
            {
              text: 'Users can update only columns that require changes.',
            },
            {
              text: (
                <div>
                  The spreadsheet should contain at least 2 columns.
                  <strong>"Charger ID" is a mandatory column</strong>.
                </div>
              ),
            },
            {
              text: 'To update a value, enter the new value in the corresponding cell within the spreadsheet.',
            },
            {
              text: 'To retain an existing value without modification, the cell should be left blank or keep the previous value.',
            },
            {
              text: <strong>To clear an existing value, the word "NULL" should be entered in the applicable cell.</strong>,
            },
            {
              text: <strong>Please keep the column names in the first row.</strong>,
            },
            {
              text: "You don't have to have all columns present.  You can only have Charger ID and columns that need to be modified.",
            },
          ],
        },
        {
          key: 'Additional Rules',
          description: [
            {
              text: (
                <div>
                  No more than <strong>5000</strong> chargers can be updated at a time.
                </div>
              ),
            },
            {
              text: 'The update can not be reversed.',
            },
            {
              text: (
                <div>
                  The file size can not be more than <strong>1,048 KB</strong>.
                </div>
              ),
            },
            {
              text: (
                <div>
                  Only <strong>.csv</strong> file format is acceptable.
                </div>
              ),
            },
          ],
        },
      ],
    },
    errorType: {
      title: 'Error Type',
      text: [
        {
          key: 'Warning',
          description: "A one-time warning. It doesn't affect the charging session.",
        },
        {
          key: 'Locked Warning',
          description: 'A persistent warning. It does not affect the charging session. Refer to Resolution Steps to fix the issue.',
        },
        {
          key: 'Warning Stops Current Session',
          description: 'A one-time warning. It stops the current charging session. Refer to Resolution Steps to fix the issue.',
        },
        {
          key: 'Error',
          description: 'A one-time error. It stops the current charging session. Refer to Resolution Steps to fix the issue.',
        },
        {
          key: 'Error Blocks All Sessions',
          description:
            'A persistent error. It stops the current charging session and prevents a new session to start. The charger is in Faulted state. Refer to Resolution Steps to fix the issue.',
        },
      ],
    },
  };
  const currentOption = customOption || get(infoOptions, infoOption, []);

  // customOption supports a flat { title, description } shape in addition to the
  // standard { title, text: [{ key, description }] } shape used by infoOption.
  const isFlatCustomOption = customOption && customOption.description !== undefined;

  const alphabeticallySortedOptions = isFlatCustomOption
    ? null
    : withoutSorting
      ? currentOption.text
      : currentOption.text?.sort((a, b) => a.key.localeCompare(b.key));

  const listOfOptions = isFlatCustomOption ? (
    <p style={{ margin: 0 }}>{customOption.description}</p>
  ) : (
    <ul className="infoButton__list">
      {alphabeticallySortedOptions?.map(({ key, description }) => {
        if (isArray(description)) {
          return (
            <React.Fragment key={key}>
              <h4 key={key}>{key}: </h4>
              {description.map((desc, index) => (
                <li key={`${key}-${index}`}>{desc.text}</li>
              ))}
            </React.Fragment>
          );
        }

        return (
          <li key={key}>
            <strong>{key}: </strong>
            {description}
          </li>
        );
      })}
    </ul>
  );

  return (
    <>
      <Tooltip
        title={currentOption && listOfOptions}
        PopperProps={{
          popperOptions: {
            modifiers: [
              {
                name: 'flip',
                options: {
                  fallbackPlacements: ['left', 'right', 'top', 'bottom'],
                },
              },
            ],
          },
        }}
      >
        <InfoIcon
          fontSize="small"
          onClick={handleClickOpen}
          style={infoIconStyle}
          ref={(el) => {
            if (el) {
              el.style.setProperty('margin-left', '5px', 'important');
            }
          }}
        />
      </Tooltip>

      <Dialog open={isOpen} onClose={handleClose}>
        <DialogTitle id="dialog-title">{currentOption && currentOption.title}</DialogTitle>
        <DialogContent>{currentOption && listOfOptions}</DialogContent>
        <DialogActions>
          <Button onClick={handleClose} autoFocus>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
